import React, { useState, useEffect } from 'react';
import { Dimension, DimensionCollection } from '../../api/OasisBackendApi';
import { useOasisBackend } from '../../hooks/useOasisBackend';
import { IconPlus, IconTrash, IconEdit, IconCheck, IconX, IconChevronDown, IconChevronRight, IconCopy, IconClipboard, IconDotsVertical, IconHistory, IconPlayerPlay } from '@tabler/icons-react';

interface DimensionTableProps {
    entityCollectionId: string;
}

const DimensionTable: React.FC<DimensionTableProps> = ({ entityCollectionId }) => {
    const db = useOasisBackend();
    const [dimensions, setDimensions] = useState<Dimension[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newDimension, setNewDimension] = useState({ 
        name: '', 
        description: '', 
        dimension_type: 'likert' as string,
        args: {} as Record<string, any>
    });
    const [creating, setCreating] = useState(false);
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const [deleting, setDeleting] = useState<string | null>(null);
    
    // New state for dimension collection creation
    const [showCreateDimensionCollection, setShowCreateDimensionCollection] = useState(false);
    const [selectedDimensions, setSelectedDimensions] = useState<Set<string>>(new Set());
    const [creatingDimensionCollection, setCreatingDimensionCollection] = useState(false);
    
    // State for copy menu
    const [showCopyMenu, setShowCopyMenu] = useState(false);
    const [copySuccess, setCopySuccess] = useState<string | null>(null);
    
    // State for paste functionality
    const [showPasteModal, setShowPasteModal] = useState(false);
    const [pasteJsonText, setPasteJsonText] = useState('');
    const [pasting, setPasting] = useState(false);
    const [pasteError, setPasteError] = useState<string | null>(null);
    const [pasteProgress, setPasteProgress] = useState<{ current: number; total: number } | null>(null);
    
    // State for options menu
    const [showOptionsMenu, setShowOptionsMenu] = useState(false);
    
    // State for collection history
    const [showCollectionHistory, setShowCollectionHistory] = useState(false);
    const [dimensionCollections, setDimensionCollections] = useState<DimensionCollection[]>([]);
    const [loadingCollections, setLoadingCollections] = useState(false);
    const [reactivatingCollection, setReactivatingCollection] = useState<number | null>(null);

    const toggleRowExpansion = (dimensionId: string) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(dimensionId)) {
            newExpandedRows.delete(dimensionId);
        } else {
            newExpandedRows.add(dimensionId);
        }
        setExpandedRows(newExpandedRows);
    };

    const fetchDimensions = async () => {
        try {
            setLoading(true);
            const response = await db.endpoints.api.apiEntityCollectionDimensionsList(entityCollectionId);
            setDimensions(response.data);
            setError(null);
        } catch (err) {
            setError('Failed to fetch dimensions');
            console.error('Error fetching dimensions:', err);
        } finally {
            setLoading(false);
        }
    };

    const createDimension = async () => {
        try {
            setCreating(true);
            const requestData: any = {
                name: newDimension.name,
                description: newDimension.description,
            };
            
            if (newDimension.dimension_type && newDimension.dimension_type !== 'likert') {
                requestData.dimension_type = newDimension.dimension_type;
            }
            
            if (newDimension.args && Object.keys(newDimension.args).length > 0) {
                requestData.args = newDimension.args;
            }
            
            const response = await db.endpoints.api.apiEntityCollectionDimensionsCreateCreate(entityCollectionId, requestData);
            
            const createdDimension = response.data;
            setDimensions([...dimensions, createdDimension]);
            setNewDimension({ name: '', description: '', dimension_type: 'likert', args: {} });
            setShowCreateForm(false);
            setError(null);
        } catch (err) {
            setError('Failed to create dimension');
            console.error('Error creating dimension:', err);
        } finally {
            setCreating(false);
        }
    };

    const deleteDimension = async (dimensionId: string) => {
        try {
            setDeleting(dimensionId);
            await db.endpoints.api.apiDimensionsDestroy(dimensionId);
            setDimensions(dimensions.filter(d => d.id !== dimensionId));
            setError(null);
        } catch (err) {
            setError('Failed to delete dimension');
            console.error('Error deleting dimension:', err);
        } finally {
            setDeleting(null);
        }
    };

    const toggleDimensionSelection = (dimensionId: string) => {
        const newSelectedDimensions = new Set(selectedDimensions);
        if (newSelectedDimensions.has(dimensionId)) {
            newSelectedDimensions.delete(dimensionId);
        } else {
            newSelectedDimensions.add(dimensionId);
        }
        setSelectedDimensions(newSelectedDimensions);
    };

    const selectAllDimensions = () => {
        setSelectedDimensions(new Set(dimensions.map(d => d.id)));
    };

    const deselectAllDimensions = () => {
        setSelectedDimensions(new Set());
    };

    const initializeDimensionSelection = () => {
        // Set live dimensions as selected by default
        const liveDimensionIds = dimensions.filter(d => d.live).map(d => d.id);
        setSelectedDimensions(new Set(liveDimensionIds));
    };

    const createDimensionCollection = async () => {
        try {
            setCreatingDimensionCollection(true);
            const dimensionIds = Array.from(selectedDimensions);
            
            const response = await db.endpoints.api.apiEntityCollectionCreateActivatedDimensionCollectionCreate(
                entityCollectionId,
                dimensionIds
            );
            
            // Refresh dimensions to show updated live status
            await fetchDimensions();
            // Refresh collection history if visible
            if (showCollectionHistory) {
                await fetchDimensionCollections();
            }
            
            // Reset state
            setShowCreateDimensionCollection(false);
            setSelectedDimensions(new Set());
            setError(null);
        } catch (err) {
            setError('Failed to create dimension collection');
            console.error('Error creating dimension collection:', err);
        } finally {
            setCreatingDimensionCollection(false);
        }
    };

    const getLiveDimensions = () => {
        return dimensions.filter(d => d.live);
    };

    const fetchDimensionCollections = async () => {
        try {
            setLoadingCollections(true);
            const response = await db.endpoints.api.apiEntityCollectionDimensionCollectionsList(entityCollectionId);
            setDimensionCollections(response.data);
        } catch (err) {
            console.error('Error fetching dimension collections:', err);
        } finally {
            setLoadingCollections(false);
        }
    };

    const reactivateCollection = async (collectionId: number) => {
        try {
            setReactivatingCollection(collectionId);
            await db.endpoints.api.apiDimensionCollectionReactivateCreate(String(collectionId));
            // Refresh both dimensions and collections
            await fetchDimensions();
            await fetchDimensionCollections();
            setError(null);
        } catch (err) {
            setError('Failed to reactivate dimension collection');
            console.error('Error reactivating collection:', err);
        } finally {
            setReactivatingCollection(null);
        }
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const copyAsList = async () => {
        const liveDimensions = getLiveDimensions();
        const dimensionNames = liveDimensions.map(d => d.name);
        const text = JSON.stringify(dimensionNames, null, 2);
        
        try {
            await navigator.clipboard.writeText(text);
            setCopySuccess('List copied to clipboard!');
            setShowCopyMenu(false);
            setTimeout(() => setCopySuccess(null), 3000);
        } catch (err) {
            setError('Failed to copy to clipboard');
            console.error('Error copying to clipboard:', err);
        }
    };

    const copyAsDictionary = async () => {
        const liveDimensions = getLiveDimensions();
        const dimensionDict = liveDimensions.map(d => ({
            id: d.id,
            name: d.name,
            description: d.description,
            live: d.live,
            indexed: d.indexed
        }));
        const text = JSON.stringify(dimensionDict, null, 2);
        
        try {
            await navigator.clipboard.writeText(text);
            setCopySuccess('Dictionary copied to clipboard!');
            setShowCopyMenu(false);
            setTimeout(() => setCopySuccess(null), 3000);
        } catch (err) {
            setError('Failed to copy to clipboard');
            console.error('Error copying to clipboard:', err);
        }
    };

    const copyAsJSON = async () => {
        const liveDimensions = getLiveDimensions();
        const dimensionCollection = liveDimensions.map(d => {
            // Check if this is a form dimension
            const isFormDimension = d.type === 'form' || d.dimension_type === 'form';
            
            if (isFormDimension) {
                // Form dimensions: include with type 'form', no args
                return {
                    name: d.name,
                    description: d.description,
                    dimension_type: 'form',
                    type: 'form' // Include type field for clarity
                };
            }
            
            // Synthetic dimensions: extract dimension_type and args
            let dimensionType = d.dimension_type || d.synthetic_configuration?.config?.dimension_type || 'likert';
            
            // Extract args from synthetic_configuration.config
            let args: Record<string, any> = {};
            if (d.synthetic_configuration?.config) {
                const config = d.synthetic_configuration.config;
                
                // Detect ordered categories: either explicitly marked or category with order in config/args
                const isOrderedCategory = dimensionType === 'ordered_category' ||
                    config.dimension_type === 'ordered_category' ||
                    (dimensionType === 'category' && (config.ordered === true || config.order || config.args?.order));
                
                if (isOrderedCategory) {
                    // Export as ordered_category so paste flow creates it correctly
                    dimensionType = 'ordered_category';
                    // Prefer top-level order/value_mapping, fall back to args
                    args.order = config.order || config.args?.order || [];
                    args.value_mapping = config.value_mapping || config.args?.value_mapping || {};
                } else if (config.args) {
                    // For other types, use the args from config
                    args = config.args;
                }
            }
            
            return {
                name: d.name,
                description: d.description,
                dimension_type: dimensionType,
                ...(Object.keys(args).length > 0 && { args })
            };
        });
        
        const text = JSON.stringify(dimensionCollection, null, 2);
        
        try {
            await navigator.clipboard.writeText(text);
            setCopySuccess('JSON copied to clipboard!');
            setShowCopyMenu(false);
            setTimeout(() => setCopySuccess(null), 3000);
        } catch (err) {
            setError('Failed to copy to clipboard');
            console.error('Error copying to clipboard:', err);
        }
    };

    const pasteAndCreateDimensions = async () => {
        if (!pasteJsonText.trim()) {
            setPasteError('Please paste JSON data');
            return;
        }

        // Parse and validate JSON
        let dimensionsToCreate: Array<{ name: string; description: string; dimension_type: string; args?: Record<string, any> }>;
        try {
            const parsed = JSON.parse(pasteJsonText);
            if (!Array.isArray(parsed)) {
                setPasteError('JSON must be an array of dimension objects');
                return;
            }
            dimensionsToCreate = parsed;
        } catch (err) {
            setPasteError('Invalid JSON format. Please check your JSON syntax.');
            return;
        }

        // Validate structure
        const errors: string[] = [];
        dimensionsToCreate.forEach((dim, index) => {
            const isFormDimension = dim.dimension_type === 'form' || (dim as any).type === 'form';
            
            if (!dim.name || typeof dim.name !== 'string') {
                errors.push(`Dimension ${index + 1}: missing or invalid 'name' field`);
            }
            // Form dimensions don't require descriptions
            if (!isFormDimension && (!dim.description || typeof dim.description !== 'string')) {
                errors.push(`Dimension ${index + 1}: missing or invalid 'description' field`);
            }
            if (!dim.dimension_type || typeof dim.dimension_type !== 'string') {
                errors.push(`Dimension ${index + 1}: missing or invalid 'dimension_type' field`);
            }
        });

        if (errors.length > 0) {
            setPasteError(`Validation errors:\n${errors.join('\n')}`);
            return;
        }

        // Separate form dimensions from synthetic dimensions
        const formDimensions = dimensionsToCreate.filter(d => 
            d.dimension_type === 'form' || (d as any).type === 'form'
        );
        const syntheticDimensions = dimensionsToCreate.filter(d => 
            d.dimension_type !== 'form' && (d as any).type !== 'form'
        );

        // Show warning if form dimensions are present
        if (formDimensions.length > 0) {
            const formNames = formDimensions.map(d => d.name).join(', ');
            alert(
                `Warning: ${formDimensions.length} form dimension(s) will be skipped:\n${formNames}\n\n` +
                `Form dimensions cannot be created through this interface as they are tied to specific columns in the entity collection. ` +
                `Only synthetic dimensions will be created.`
            );
        }

        // Clear errors and start creating (only synthetic dimensions)
        setPasteError(null);
        setPasting(true);
        setPasteProgress({ current: 0, total: syntheticDimensions.length });

        const createdDimensionIds: string[] = [];
        const failedDimensions: Array<{ name: string; error: string }> = [];
        const skippedDimensions = formDimensions.map(d => d.name);

        // Create dimensions sequentially (only synthetic)
        for (let i = 0; i < syntheticDimensions.length; i++) {
            const dim = syntheticDimensions[i];
            setPasteProgress({ current: i + 1, total: syntheticDimensions.length });

            try {
                const requestData: any = {
                    name: dim.name,
                    description: dim.description,
                };

                if (dim.dimension_type && dim.dimension_type !== 'likert') {
                    requestData.dimension_type = dim.dimension_type;
                }

                if (dim.args && Object.keys(dim.args).length > 0) {
                    requestData.args = dim.args;
                }

                const response = await db.endpoints.api.apiEntityCollectionDimensionsCreateCreate(
                    entityCollectionId,
                    requestData
                );

                createdDimensionIds.push(response.data.id);
            } catch (err: any) {
                const errorMessage = err?.response?.data?.error || err?.message || 'Unknown error';
                failedDimensions.push({ name: dim.name, error: errorMessage });
            }
        }

        setPasting(false);
        setPasteProgress(null);

        // Refresh dimensions list
        await fetchDimensions();

        // Build result message
        let resultMessage = '';
        if (skippedDimensions.length > 0) {
            resultMessage += `Skipped ${skippedDimensions.length} form dimension(s): ${skippedDimensions.join(', ')}\n\n`;
        }

        // Show results
        if (failedDimensions.length === 0) {
            const successMsg = resultMessage + `Successfully created ${createdDimensionIds.length} synthetic dimension(s)!`;
            setCopySuccess(successMsg);
            setTimeout(() => setCopySuccess(null), 5000);
            setShowPasteModal(false);
            setPasteJsonText('');
        } else if (createdDimensionIds.length > 0) {
            setError(
                resultMessage +
                `Created ${createdDimensionIds.length} dimension(s), but ${failedDimensions.length} failed:\n` +
                failedDimensions.map(f => `- ${f.name}: ${f.error}`).join('\n')
            );
        } else {
            setPasteError(
                resultMessage +
                `Failed to create all dimensions:\n` +
                failedDimensions.map(f => `- ${f.name}: ${f.error}`).join('\n')
            );
        }
    };

    useEffect(() => {
        fetchDimensions();
    }, [entityCollectionId, db.endpoints]);

    useEffect(() => {
        if (showCreateDimensionCollection && dimensions.length > 0) {
            initializeDimensionSelection();
        }
    }, [showCreateDimensionCollection, dimensions]);

    useEffect(() => {
        if (showCollectionHistory) {
            fetchDimensionCollections();
        }
    }, [showCollectionHistory]);

    // Close menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (showCopyMenu && !target.closest('.copy-menu-container')) {
                setShowCopyMenu(false);
            }
            if (showOptionsMenu && !target.closest('.options-menu-container')) {
                setShowOptionsMenu(false);
            }
        };

        if (showCopyMenu || showOptionsMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showCopyMenu, showOptionsMenu]);

    if (loading) {
        return (
            <div className="flex justify-center items-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-900/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg">
                {error}
            </div>
        );
    }

    return (
        <div className="bg-gray-900 rounded-xl shadow-2xl border border-gray-800">

            {/* Collection History Section */}
            <div className="border-t border-gray-800">
                <button
                    onClick={() => setShowCollectionHistory(!showCollectionHistory)}
                    className="w-full px-6 py-3 flex items-center justify-between hover:bg-gray-800/50 transition-colors"
                >
                    <div className="flex items-center space-x-2">
                        <IconHistory className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-300">Collection History</span>
                        {dimensionCollections.length > 0 && (
                            <span className="text-xs bg-gray-700 text-gray-400 px-2 py-0.5 rounded-full">
                                {dimensionCollections.length}
                            </span>
                        )}
                    </div>
                    {showCollectionHistory ? (
                        <IconChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                        <IconChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                </button>

                {showCollectionHistory && (
                    <div className="px-6 pb-4">
                        {loadingCollections ? (
                            <div className="flex justify-center items-center py-6">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
                            </div>
                        ) : dimensionCollections.length === 0 ? (
                            <div className="text-sm text-gray-500 py-4 text-center">
                                No dimension collections yet.
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {dimensionCollections.map((collection) => (
                                    <div
                                        key={collection.id}
                                        className={`rounded-lg border p-4 transition-all duration-200 ${
                                            collection.live
                                                ? 'border-green-500/40 bg-green-900/10'
                                                : 'border-gray-700 bg-gray-800/30 hover:border-gray-600'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center space-x-2 mb-1">
                                                    <span className="text-sm font-medium text-gray-200">
                                                        Collection #{collection.id}
                                                    </span>
                                                    {collection.live && (
                                                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full border border-green-500/30">
                                                            Live
                                                        </span>
                                                    )}
                                                    {collection.created_at && (
                                                        <span className="text-xs text-gray-500">
                                                            Created {formatDate(collection.created_at)}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex flex-wrap gap-1.5 mb-2">
                                                    {collection.dimensions.map((dim) => (
                                                        <span
                                                            key={dim.id}
                                                            className="text-xs bg-gray-700/60 text-gray-300 px-2 py-0.5 rounded"
                                                        >
                                                            {dim.name}
                                                        </span>
                                                    ))}
                                                </div>
                                                {/* Live History Timeline */}
                                                {collection.live_history && collection.live_history.length > 0 && (
                                                    <div className="mt-2">
                                                        <div className="text-xs text-gray-500 mb-1">Live periods:</div>
                                                        <div className="space-y-1">
                                                            {collection.live_history.map((period) => (
                                                                <div
                                                                    key={period.id}
                                                                    className="flex items-center text-xs"
                                                                >
                                                                    <div className={`w-1.5 h-1.5 rounded-full mr-2 ${
                                                                        period.went_offline_at === null
                                                                            ? 'bg-green-400 animate-pulse'
                                                                            : 'bg-gray-500'
                                                                    }`} />
                                                                    <span className="text-gray-400">
                                                                        {formatDate(period.went_live_at)}
                                                                    </span>
                                                                    <span className="text-gray-600 mx-1.5">&rarr;</span>
                                                                    <span className={!period.went_offline_at ? 'text-green-400' : 'text-gray-400'}>
                                                                        {!period.went_offline_at
                                                                            ? 'Now'
                                                                            : formatDate(period.went_offline_at)}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {/* Reactivate Button */}
                                            {!collection.live && (
                                                <button
                                                    onClick={() => reactivateCollection(collection.id)}
                                                    disabled={reactivatingCollection !== null}
                                                    className="ml-3 flex items-center px-3 py-1.5 text-sm bg-orange-600/20 text-orange-400 border border-orange-500/30 rounded-lg hover:bg-orange-600/30 hover:border-orange-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                                >
                                                    {reactivatingCollection === collection.id ? (
                                                        <>
                                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-400 mr-1.5"></div>
                                                            Reactivating...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <IconPlayerPlay className="w-3.5 h-3.5 mr-1.5" />
                                                            Reactivate
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className="px-6 py-4 border-b border-gray-800">
                <div className="flex justify-between items-center">
                    <div>
                        <div className="flex items-center space-x-3">
                            <h2 className="text-xl font-semibold text-gray-100">Dimensions</h2>
                            <span className="text-xs bg-gray-700 text-gray-400 px-2 py-0.5 rounded-full">
                                {dimensions.filter(d => d.live).length} live / {dimensions.length} total
                            </span>
                        </div>
                        <p className="text-sm text-gray-400 mt-1">Dimensions are used to create the interpretable Oasis Vectors used at search time. You can add new dimensions and when you reindex your data they will be tested for accuracy.</p>
                    </div>
                    <div className="flex space-x-3">
                        {showCreateDimensionCollection && (
                            <>
                                <button
                                    onClick={createDimensionCollection}
                                    disabled={creatingDimensionCollection || selectedDimensions.size === 0}
                                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                                >
                                    {creatingDimensionCollection ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <IconCheck className="w-4 h-4 mr-2" />
                                            Submit New Dimension Collection
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowCreateDimensionCollection(false);
                                        setSelectedDimensions(new Set());
                                    }}
                                    className="flex items-center px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-all duration-200"
                                >
                                    <IconX className="w-4 h-4 mr-2" />
                                    Cancel
                                </button>
                            </>
                        )}
                        {!showCreateDimensionCollection && (
                            <div className="relative options-menu-container">
                                <button
                                    onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                                    className="flex items-center justify-center p-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-all duration-200 border border-gray-700 hover:border-gray-600"
                                    title="Options"
                                >
                                    <IconDotsVertical className="w-5 h-5" />
                                </button>
                                {showOptionsMenu && (
                                    <div className="absolute right-0 mt-2 w-56 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-20">
                                        <div className="py-1">
                                            <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-700">
                                                Copy Dimensions
                                            </div>
                                            <button
                                                onClick={() => {
                                                    copyAsList();
                                                    setShowOptionsMenu(false);
                                                }}
                                                disabled={getLiveDimensions().length === 0}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-100 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                                            >
                                                <IconCopy className="w-4 h-4 mr-2" />
                                                Copy as List
                                            </button>
                                            <button
                                                onClick={() => {
                                                    copyAsDictionary();
                                                    setShowOptionsMenu(false);
                                                }}
                                                disabled={getLiveDimensions().length === 0}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-100 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                                            >
                                                <IconCopy className="w-4 h-4 mr-2" />
                                                Copy as Dictionary
                                            </button>
                                            <button
                                                onClick={() => {
                                                    copyAsJSON();
                                                    setShowOptionsMenu(false);
                                                }}
                                                disabled={getLiveDimensions().length === 0}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-100 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                                            >
                                                <IconCopy className="w-4 h-4 mr-2" />
                                                Copy as JSON
                                            </button>
                                            <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider border-t border-gray-700 mt-1">
                                                Create Dimensions
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setShowPasteModal(true);
                                                    setShowOptionsMenu(false);
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-100 hover:bg-gray-700 transition-colors flex items-center"
                                            >
                                                <IconClipboard className="w-4 h-4 mr-2" />
                                                Paste from JSON
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowCreateForm(true);
                                                    setShowOptionsMenu(false);
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-100 hover:bg-gray-700 transition-colors flex items-center"
                                            >
                                                <IconPlus className="w-4 h-4 mr-2" />
                                                Add Dimension
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowCreateDimensionCollection(true);
                                                    setShowOptionsMenu(false);
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-100 hover:bg-gray-700 transition-colors flex items-center"
                                            >
                                                <IconPlus className="w-4 h-4 mr-2" />
                                                Create Dimension Collection
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            {copySuccess && (
                <div className="px-6 py-2 bg-green-900/20 border-b border-green-500/30">
                    <div className="text-green-400 text-sm">{copySuccess}</div>
                </div>
            )}

            {showCreateForm && (
                <div className="px-6 py-4 border-b border-gray-800 bg-gray-800/50">
                    <h3 className="text-lg font-medium text-gray-100 mb-4">Create New Dimension</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Name
                            </label>
                            <input
                                type="text"
                                value={newDimension.name}
                                onChange={(e) => setNewDimension({ ...newDimension, name: e.target.value })}
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-100 placeholder-gray-500"
                                placeholder="Enter dimension name"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Description
                            </label>
                            <textarea
                                value={newDimension.description}
                                onChange={(e) => setNewDimension({ ...newDimension, description: e.target.value })}
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-100 placeholder-gray-500"
                                rows={3}
                                placeholder="Enter dimension description"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Dimension Type
                            </label>
                            <select
                                value={newDimension.dimension_type}
                                onChange={(e) => setNewDimension({ ...newDimension, dimension_type: e.target.value, args: {} })}
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-100"
                            >
                                <option value="likert">Likert</option>
                                <option value="timestamp">Timestamp</option>
                                <option value="number">Number</option>
                                <option value="boolean">Boolean</option>
                                <option value="ordered_category">Ordered Category</option>
                            </select>
                        </div>
                        {newDimension.dimension_type === 'ordered_category' && (
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Order (comma-separated values)
                                    </label>
                                    <input
                                        type="text"
                                        value={Array.isArray(newDimension.args?.order) ? newDimension.args.order.join(', ') : ''}
                                        onChange={(e) => {
                                            const order = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                                            setNewDimension({
                                                ...newDimension,
                                                args: { ...newDimension.args, order }
                                            });
                                        }}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-100 placeholder-gray-500"
                                        placeholder="e.g., low, medium, high"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Value Mapping (JSON object)
                                    </label>
                                    <textarea
                                        value={newDimension.args?.value_mapping ? JSON.stringify(newDimension.args.value_mapping, null, 2) : ''}
                                        onChange={(e) => {
                                            try {
                                                const value_mapping = e.target.value ? JSON.parse(e.target.value) : {};
                                                setNewDimension({
                                                    ...newDimension,
                                                    args: { ...newDimension.args, value_mapping }
                                                });
                                            } catch (err) {
                                                // Invalid JSON, don't update
                                            }
                                        }}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-100 placeholder-gray-500 font-mono text-sm"
                                        rows={4}
                                        placeholder='{"low": -1, "medium": 0, "high": 1}'
                                    />
                                </div>
                            </div>
                        )}
                        <div className="flex space-x-3">
                            <button
                                onClick={createDimension}
                                disabled={creating || !newDimension.name || !newDimension.description}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                            >
                                {creating ? 'Creating...' : 'Create Dimension'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowCreateForm(false);
                                    setNewDimension({ name: '', description: '', dimension_type: 'likert', args: {} });
                                }}
                                className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-all duration-200"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showCreateDimensionCollection && (
                <div className="px-6 py-4 border-b border-gray-800 bg-green-900/20">
                    <h3 className="text-lg font-medium text-green-100 mb-2">Create New Dimension Collection</h3>
                    <p className="text-sm text-green-300 mb-3">
                        Select the dimensions you want to include in the new dimension collection. 
                        Live dimensions are selected by default but can be unchecked. 
                        Non-live dimensions can be selected to include them in the new collection.
                    </p>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={selectAllDimensions}
                            className="px-3 py-1.5 text-sm font-medium text-green-100 bg-green-800/50 hover:bg-green-700/50 border border-green-600/50 rounded-lg transition-colors"
                        >
                            Select all
                        </button>
                        <button
                            type="button"
                            onClick={deselectAllDimensions}
                            className="px-3 py-1.5 text-sm font-medium text-green-100 bg-green-800/50 hover:bg-green-700/50 border border-green-600/50 rounded-lg transition-colors"
                        >
                            Deselect all
                        </button>
                    </div>
                </div>
            )}

            <div className="p-6">
                {dimensions.length === 0 ? (
                    <div className="text-center py-8">
                        <div className="text-gray-500 text-lg mb-2">No dimensions found</div>
                        <div className="text-gray-600 text-sm">Index your entities to create dimensions!</div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Header */}
                        <div className={`grid gap-4 px-4 py-3 bg-gray-800/50 rounded-lg border border-gray-700 ${showCreateDimensionCollection ? 'grid-cols-12' : 'grid-cols-12'}`}>
                            {showCreateDimensionCollection && (
                                <div className="col-span-1">
                                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Select</span>
                                </div>
                            )}
                            <div className={showCreateDimensionCollection ? "col-span-3" : "col-span-4"}>
                                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Name</span>
                            </div>
                            <div className={showCreateDimensionCollection ? "col-span-3" : "col-span-4"}>
                                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Description</span>
                            </div>
                            <div className="col-span-1">
                                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Live</span>
                            </div>
                            <div className="col-span-1">
                                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Indexed</span>
                            </div>
                            <div className="col-span-2">
                                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</span>
                            </div>
                        </div>

                        {/* Dimension Cards */}
                        {dimensions
                            .sort((a, b) => {
                                // Sort by live status first (live dimensions come first)
                                if (a.live && !b.live) return -1;
                                if (!a.live && b.live) return 1;
                                // Then sort by name alphabetically
                                return a.name.localeCompare(b.name);
                            })
                            .map((dimension) => {
                                const isExpanded = expandedRows.has(dimension.id);
                                return (
                                    <div key={dimension.id} className="space-y-0">
                                        {/* Main Row */}
                                        <div 
                                            className={`grid gap-4 px-4 py-4 bg-gray-800/30 rounded-lg border border-gray-700 hover:bg-gray-800/50 transition-all duration-200 hover:border-gray-600 ${showCreateDimensionCollection ? 'grid-cols-12' : 'grid-cols-12 cursor-pointer'}`}
                                            onClick={showCreateDimensionCollection ? undefined : () => toggleRowExpansion(dimension.id)}
                                        >
                                            {showCreateDimensionCollection && (
                                                <div className="col-span-1 flex items-center justify-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedDimensions.has(dimension.id)}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            toggleDimensionSelection(dimension.id);
                                                        }}
                                                        className="w-4 h-4 text-green-600 bg-gray-800 border-gray-600 rounded focus:ring-green-500 focus:ring-2"
                                                    />
                                                </div>
                                            )}
                                            <div className={showCreateDimensionCollection ? "col-span-3" : "col-span-4"} 
                                                 onClick={showCreateDimensionCollection ? () => toggleRowExpansion(dimension.id) : undefined}
                                                 style={{ cursor: showCreateDimensionCollection ? 'pointer' : 'inherit' }}>
                                                <div className="flex items-center space-x-2 min-w-0">
                                                    <IconChevronRight 
                                                        className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                                                    />
                                                    <span className="text-sm font-medium text-gray-100 truncate">
                                                        {dimension.name}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className={showCreateDimensionCollection ? "col-span-3" : "col-span-4"} 
                                                 onClick={showCreateDimensionCollection ? () => toggleRowExpansion(dimension.id) : undefined}
                                                 style={{ cursor: showCreateDimensionCollection ? 'pointer' : 'inherit' }}>
                                                <span className="text-sm text-gray-400 line-clamp-1">
                                                    {dimension.description}
                                                </span>
                                            </div>
                                            <div className="col-span-1 flex items-center justify-center">
                                                {dimension.live ? (
                                                    <IconCheck className="w-5 h-5 text-green-400" />
                                                ) : (
                                                    <IconX className="w-5 h-5 text-red-400" />
                                                )}
                                            </div>
                                            <div className="col-span-1 flex items-center justify-center">
                                                {dimension.indexed ? (
                                                    <IconCheck className="w-5 h-5 text-green-400" />
                                                ) : (
                                                    <IconX className="w-5 h-5 text-red-400" />
                                                )}
                                            </div>
                                            <div className="col-span-2 flex items-center justify-end space-x-2">
                                                {!dimension.live && !showCreateDimensionCollection && (
                                                    <button 
                                                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all duration-200"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            deleteDimension(dimension.id);
                                                        }}
                                                        disabled={deleting === dimension.id}
                                                    >
                                                        {deleting === dimension.id ? (
                                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-400"></div>
                                                        ) : (
                                                            <IconTrash className="w-4 h-4" />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Expanded Details */}
                                        {isExpanded && (
                                            <div className="px-4 py-4 bg-gray-800/20 rounded-lg border border-gray-700 ml-4 -mt-2 mb-2">
                                                <div className="space-y-4">
                                                    <div className="grid grid-cols-2 gap-6">
                                                        <div>
                                                            <h4 className="text-sm font-medium text-gray-300 mb-2">Full Name</h4>
                                                            <p className="text-sm text-gray-100 bg-gray-800/50 px-3 py-2 rounded-lg border border-gray-700">
                                                                {dimension.name}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <h4 className="text-sm font-medium text-gray-300 mb-2">Status</h4>
                                                            <div className="flex items-center space-x-4">
                                                                <div className="flex items-center space-x-2">
                                                                    <span className="text-sm text-gray-400">Live:</span>
                                                                    {dimension.live ? (
                                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-900/30 text-green-400 border border-green-500/30">
                                                                            <IconCheck className="w-3 h-3 mr-1" />
                                                                            Active
                                                                        </span>
                                                                    ) : (
                                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-900/30 text-red-400 border border-red-500/30">
                                                                            <IconX className="w-3 h-3 mr-1" />
                                                                            Inactive
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center space-x-2">
                                                                    <span className="text-sm text-gray-400">Indexed:</span>
                                                                    {dimension.indexed ? (
                                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-900/30 text-green-400 border border-green-500/30">
                                                                            <IconCheck className="w-3 h-3 mr-1" />
                                                                            Yes
                                                                        </span>
                                                                    ) : (
                                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-900/30 text-red-400 border border-red-500/30">
                                                                            <IconX className="w-3 h-3 mr-1" />
                                                                            No
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <h4 className="text-sm font-medium text-gray-300 mb-2">Full Description</h4>
                                                        <p className="text-sm text-gray-100 bg-gray-800/50 px-3 py-2 rounded-lg border border-gray-700 whitespace-pre-wrap">
                                                            {dimension.description || 'No description provided'}
                                                        </p>
                                                    </div>
                                                    <div className={`grid gap-6 ${dimension.synthetic_configuration?.config?.args && Object.keys(dimension.synthetic_configuration.config.args).length > 0 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                                                        <div>
                                                            <h4 className="text-sm font-medium text-gray-300 mb-2">Dimension Type</h4>
                                                            <p className="text-sm text-gray-100 bg-gray-800/50 px-3 py-2 rounded-lg border border-gray-700">
                                                                {dimension.dimension_type || dimension.type || 'N/A'}
                                                            </p>
                                                        </div>
                                                        {dimension.synthetic_configuration?.config?.args && Object.keys(dimension.synthetic_configuration.config.args).length > 0 && (
                                                            <div>
                                                                <h4 className="text-sm font-medium text-gray-300 mb-2">Args</h4>
                                                                <div className="text-sm text-gray-100 bg-gray-800/50 px-3 py-2 rounded-lg border border-gray-700">
                                                                    {dimension.dimension_type === 'ordered_category' && dimension.synthetic_configuration.config.order ? (
                                                                        <div className="space-y-2">
                                                                            {dimension.synthetic_configuration.config.order && (
                                                                                <div>
                                                                                    <span className="text-gray-400">Order: </span>
                                                                                    <span className="text-gray-200">{dimension.synthetic_configuration.config.order.join(', ')}</span>
                                                                                </div>
                                                                            )}
                                                                            {dimension.synthetic_configuration.config.value_mapping && (
                                                                                <div>
                                                                                    <span className="text-gray-400">Value Mapping: </span>
                                                                                    <pre className="text-xs whitespace-pre-wrap font-mono mt-1">
                                                                                        {JSON.stringify(dimension.synthetic_configuration.config.value_mapping, null, 2)}
                                                                                    </pre>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <pre className="text-xs whitespace-pre-wrap font-mono">
                                                                            {JSON.stringify(dimension.synthetic_configuration.config.args, null, 2)}
                                                                        </pre>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex justify-end space-x-2 pt-2 border-t border-gray-700">
                                                        {!dimension.live && (
                                                            <button 
                                                                className="px-3 py-1 text-sm text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded transition-all duration-200"
                                                                onClick={() => deleteDimension(dimension.id)}
                                                                disabled={deleting === dimension.id}
                                                            >
                                                                {deleting === dimension.id ? (
                                                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-400 mr-1 inline"></div>
                                                                ) : (
                                                                    <IconTrash className="w-4 h-4 mr-1 inline" />
                                                                )}
                                                                Delete
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                )}
            </div>


            {/* Paste from JSON Modal */}
            {showPasteModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-700">
                            <h3 className="text-lg font-semibold text-gray-100">Paste Dimension Collection JSON</h3>
                            <p className="text-sm text-gray-400 mt-1">
                                Paste the JSON array of dimensions to create them all at once. Each dimension should have name, description, dimension_type, and optional args.
                            </p>
                        </div>
                        <div className="px-6 py-4 flex-1 overflow-hidden flex flex-col">
                            {pasteError && (
                                <div className="mb-4 p-3 bg-red-900/20 border border-red-500/30 text-red-400 rounded-lg text-sm whitespace-pre-wrap">
                                    {pasteError}
                                </div>
                            )}
                            {pasteProgress && (
                                <div className="mb-4 p-3 bg-blue-900/20 border border-blue-500/30 text-blue-400 rounded-lg text-sm">
                                    Creating dimension {pasteProgress.current} of {pasteProgress.total}...
                                </div>
                            )}
                            <textarea
                                value={pasteJsonText}
                                onChange={(e) => {
                                    setPasteJsonText(e.target.value);
                                    setPasteError(null);
                                }}
                                className="flex-1 w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-gray-100 placeholder-gray-500 font-mono text-sm resize-none"
                                placeholder='[\n  {\n    "name": "dimension_name",\n    "description": "dimension description",\n    "dimension_type": "likert",\n    "args": {}\n  }\n]'
                                disabled={pasting}
                            />
                        </div>
                        <div className="px-6 py-4 border-t border-gray-700 flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowPasteModal(false);
                                    setPasteJsonText('');
                                    setPasteError(null);
                                    setPasteProgress(null);
                                }}
                                disabled={pasting}
                                className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={pasteAndCreateDimensions}
                                disabled={pasting || !pasteJsonText.trim()}
                                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center"
                            >
                                {pasting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <IconCheck className="w-4 h-4 mr-2" />
                                        Create Dimensions
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DimensionTable;